import React from 'react';
import { Routes, Route } from 'react-router-dom';
import App from './App'; // Private area with layout and protected routes
import SignIn from './pages/SignIn';
import Register from './pages/Register';
import VerifyEmail from './components/VerifyEmail';
import AccountStatusGate from './components/AccountStatusGate';
import PublicRoute from './components/PublicRoute';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import Groups from './pages/masterfiles/Groups';
import Stores from './pages/masterfiles/Stores';
import Personnel from './pages/masterfiles/Personnel';
import GeneratePayroll from './pages/payroll/GeneratePayroll';
import ManagePayroll from './pages/payroll/ManagePayroll';
import Deductions from './pages/payroll/Deductions';
import SavePayrolls from './pages/payroll/SavePayrolls';
import ViewPayroll from './pages/payroll/ViewPayroll';
import Payslips from './pages/payroll/Payslips';
import PayslipView from './pages/payroll/PayslipView';
import GovernmentBenefits from './pages/reports/fgi/GovernmentBenefits';
import StoreBenefits from './pages/reports/fgi/StoreBenefits';
import PersonnelBenefits from './pages/reports/fgi/PersonnelBenefits';
import { Navigate } from 'react-router-dom';
import Employee from './pages/Employee';
import ThirteenthMonthPay from './pages/payroll/ThirteenthMonthPay';
import ViewThirteenthMonthPayroll from './pages/payroll/ThirteenthMonthPay/ViewThirteenthMonthPayroll';
import ThirteenthMonthPayslipView from './pages/payroll/ThirteenthMonthPay/ThirteenthMonthPayslip';
import { useAuth } from './context/AuthContext';
import { getAccessibleDeductionDepartments, getDefaultDeductionPath } from './pages/payroll/Deductions/access';

// Lazy editor pages
const EditPayrollPage = React.lazy(() => import('./pages/payroll/EditPayroll'));

const AppRoutes = () => {
  const { user } = useAuth();

  const userPosition = user?.position || '';
  const isFGIUser = userPosition.toLowerCase().includes('fgi');
  const payrollAccessControl = user?.payrollAccessControl || {};

  const canSeeMasterfile = payrollAccessControl.masterfile !== false;
  const canSeePayroll = payrollAccessControl.payroll !== false;
  const canSeeEmployees = payrollAccessControl.employees !== false;
  const canSeeReports = payrollAccessControl.reports !== false;

  const accessibleDeductionDepartments = getAccessibleDeductionDepartments(user);
  const defaultDeductionPath = getDefaultDeductionPath(user);

  const defaultPrivatePath =
    !isFGIUser && canSeePayroll ? '/payroll/manage'
    : user?.role !== 'store' && accessibleDeductionDepartments.length > 0 ? defaultDeductionPath
    : !isFGIUser && canSeeMasterfile ? '/masterfiles/groups'
    : user?.role !== 'store' && !isFGIUser && canSeeEmployees ? '/employees'
    : canSeeReports ? '/reports/government-benefits'
    : '/account-status';

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/account-status" element={<AccountStatusGate />} />
      <Route path="/payslip/:payrollId/:employeeId" element={<PayslipView />} />
      <Route path="/13thmonthpayslip/:employeeId" element={<ThirteenthMonthPayslipView />} />

      {/* Private routes (all other paths) */}
      <Route path="/*" element={<App />}>
        <Route index element={<ProtectedRoute><Navigate to={defaultPrivatePath} replace /></ProtectedRoute>} />
        <Route path="masterfiles/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
        <Route path="masterfiles/stores" element={<ProtectedRoute><Stores /></ProtectedRoute>} />
        <Route path="masterfiles/personnel" element={<ProtectedRoute><Personnel /></ProtectedRoute>} />
        <Route path="payroll/generate" element={<ProtectedRoute><GeneratePayroll /></ProtectedRoute>} />
        <Route path="payroll/manage" element={<ProtectedRoute><ManagePayroll /></ProtectedRoute>} />
        <Route path="deductions" element={<ProtectedRoute><Navigate to={defaultDeductionPath} replace /></ProtectedRoute>} />
        <Route
          path="deductions/:department/store"
          element={
            <ProtectedRoute>
              {accessibleDeductionDepartments.length > 0 ? <Deductions /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="deductions/:department"
          element={
            <ProtectedRoute>
              {accessibleDeductionDepartments.length > 0 ? <Deductions /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route path="payroll/:id/edit" element={<ProtectedRoute><React.Suspense fallback={null}><EditPayrollPage /></React.Suspense></ProtectedRoute>} />
        <Route path="payroll/:id/view" element={<ProtectedRoute><React.Suspense fallback={null}><EditPayrollPage /></React.Suspense></ProtectedRoute>} />
        <Route path="payroll/save" element={<ProtectedRoute><SavePayrolls /></ProtectedRoute>} />
        <Route path="payroll/:id/billing" element={<ProtectedRoute><ViewPayroll /></ProtectedRoute>} />
        <Route path="payroll/payslips" element={<ProtectedRoute><Payslips /></ProtectedRoute>} />
        <Route path="payroll/13thmonth" element={<ProtectedRoute><ThirteenthMonthPay /></ProtectedRoute>} />
        <Route path="payroll/13thmonth/view/:id" element={<ProtectedRoute><React.Suspense fallback={null}><ViewThirteenthMonthPayroll /></React.Suspense></ProtectedRoute>} />
        <Route path="reports/government-benefits" element={<ProtectedRoute><GovernmentBenefits /></ProtectedRoute>} />
        <Route path="reports/government-benefits/store" element={<ProtectedRoute><StoreBenefits /></ProtectedRoute>} />
        <Route path="reports/government-benefits/personnel" element={<ProtectedRoute><PersonnelBenefits /></ProtectedRoute>} />
        <Route path="employees" element={<ProtectedRoute><Employee /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to={defaultPrivatePath} replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;


